import React, { useState } from "react";
import "./style.scss";
import { useParams } from "react-router-dom";
import { Alert, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { useInstallationTest } from "../../../hooks/useInstallationTest";
import { useLiveDataSession } from "../../../hooks/useLiveDataSession";
import LiveData from "./LiveData";
import LiveGraph from "./LiveGraph";
import { TestStartSection } from "./components/TestStartSection";
import { TestRunningSection } from "./components/TestRunningSection";
import { TestResultsSection } from "./components/TestResultsSection";

type TabKey = "live_data" | "live_graph" | "device_test";

const InstallationTest: React.FC = () => {
  const { device_id } = useParams<{ device_id: string }>();
  const { t } = useTranslation("settings");
  const [activeTab, setActiveTab] = useState<TabKey>("live_data");

  const liveDataSession = useLiveDataSession({
    deviceId: device_id,
    autoRefresh: true,
    refreshInterval: 5000,
  });

  const {
    status,
    loading,
    error,
    startTest,
    stopTest,
    clearError,
    getElapsedTime,
  } = useInstallationTest({
    deviceId: device_id,
    enabled: activeTab === "device_test",
  });

  const testSummary = status?.install_test_summary;

  const tabItems = [
    {
      key: "live_data",
      label: t("live_data"),
      children: (
        <div className="installation-test-tab-pane">
          <LiveData liveDataSession={liveDataSession} />
        </div>
      ),
    },
    {
      key: "live_graph",
      label: t("live_graph"),
      children: (
        <div className="installation-test-tab-pane live-graph-wrapper">
          <LiveGraph liveDataStatus={liveDataSession.status} />
        </div>
      ),
    },
    {
      key: "device_test",
      label: t("installation_test"),
      children: (
        <div className="installation-test-tab-pane test-content">
          {error && (
            <Alert
              message={t("error")}
              description={error}
              type="error"
              showIcon
              closable
              onClose={clearError}
              style={{ marginBottom: 20 }}
            />
          )}

          {status?.install_test && !status?.has_results && (
            <TestRunningSection
              onStopTest={stopTest}
              loading={loading}
              elapsedTime={getElapsedTime()}
            />
          )}

          {!status?.install_test && status?.has_results && testSummary && (
            <>
              <TestResultsSection results={testSummary} />
              <TestStartSection
                onStartTest={startTest}
                loading={loading}
                hasResults={status?.has_results}
              />
            </>
          )}

          {!status?.install_test && !status?.has_results && (
            <TestStartSection
              onStartTest={startTest}
              loading={loading}
              hasResults={status?.has_results}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="installation-test">
      <div className="installation-test-container">
        <div className="installation-test-tabs-wrapper">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as TabKey)}
            items={tabItems}
            className="installation-test-tabs"
          />
        </div>
      </div>
    </div>
  );
};

export default InstallationTest;
